import type { WikiArticle } from './wikiTypes'

export const adminArticles: WikiArticle[] = [
  {
    id: 'admin-1',
    categoryId: 'admin', categoryLabel: 'Administration', categoryColor: '#6B6050',
    guideNumber: 1, totalGuides: 4,
    title: 'Gestion des rôles',
    subtitle: 'Comprendre les permissions de chaque rôle et qui peut faire quoi sur TremorApp.',
    readTime: '5 min',
    nextArticle: 'admin-2',
    sections: [
      {
        title: 'Les 3 rôles TremorApp',
        content: 'TremorApp utilise un système de contrôle d\'accès basé sur 3 rôles. Chaque utilisateur d\'un workspace se voit attribuer un rôle qui détermine ses droits sur toutes les fonctionnalités.',
        table: {
          headers: ['Rôle', 'Description', 'Qui l\'attribue'],
          rows: [
            ['Admin', 'Accès complet — gère le workspace, les membres, les intégrations et le billing', 'Premier utilisateur du workspace. Peut déléguer.'],
            ['Manager', 'Accès opérationnel — utilise tous les modules, crée et modifie les configurations', 'Admin uniquement'],
            ['Viewer', 'Lecture seule — consulte les données et rapports sans pouvoir modifier', 'Admin uniquement'],
          ],
        },
        screenshotHint: '📸 Capture : page Équipe montrant les 3 rôles avec leurs badges de couleur',
      },
      {
        title: 'Matrice des permissions',
        content: 'Voici le détail de ce que chaque rôle peut et ne peut pas faire.',
        table: {
          headers: ['Fonctionnalité', 'Admin', 'Manager', 'Viewer'],
          rows: [
            ['Consulter les dashboards et rapports', '✓', '✓', '✓'],
            ['Exporter les données (CSV, PDF)', '✓', '✓', '✓'],
            ['Créer / modifier des Radars (LLR)', '✓', '✓', '✗'],
            ['Gérer les alertes et les traiter', '✓', '✓', '✗'],
            ['Utiliser les actions IA', '✓', '✓', '✗'],
            ['Répondre aux avis Google', '✓', '✓', '✗'],
            ['Créer / lancer des campagnes ARE', '✓', '✓', '✗'],
            ['Créer / modifier des marchés SDI', '✓', '✓', '✗'],
            ['Configurer les connecteurs (Ads, GSC...)', '✓', '✓', '✗'],
            ['Inviter et gérer les membres', '✓', '✗', '✗'],
            ['Modifier les paramètres du workspace', '✓', '✗', '✗'],
            ['Gérer le billing et l\'abonnement', '✓', '✗', '✗'],
          ],
        },
        tip: { type: 'info', content: 'Le rôle Viewer est idéal pour les clients finaux, les décideurs qui consultent les résultats, les membres en onboarding ou les prestataires externes.' },
      },
      {
        title: 'Changer le rôle d\'un membre',
        content: 'Vous devez être Admin pour modifier les rôles.',
        steps: [
          { number: 1, title: 'Accéder', description: 'Allez dans la sidebar → "Équipe"' },
          { number: 2, title: 'Trouver', description: 'Trouvez le membre dont vous souhaitez modifier le rôle' },
          { number: 3, title: 'Cliquer', description: 'Cliquez sur le badge de rôle à côté de son nom' },
          { number: 4, title: 'Sélectionner', description: 'Choisissez le nouveau rôle dans le menu déroulant' },
          { number: 5, title: 'Confirmer', description: 'Le changement est effectif immédiatement' },
        ],
        tip: { type: 'warning', content: 'Vous ne pouvez pas vous rétrograder vous-même si vous êtes le seul Admin du workspace. Désignez d\'abord un autre Admin.' },
        screenshotHint: '📸 Capture : dropdown de changement de rôle ouvert sur un membre',
      },
    ],
  },
  {
    id: 'admin-2',
    categoryId: 'admin', categoryLabel: 'Administration', categoryColor: '#6B6050',
    guideNumber: 2, totalGuides: 4,
    title: 'Inviter et gérer les membres',
    subtitle: 'Envoyer des invitations, gérer les rôles et désactiver un compte membre.',
    readTime: '5 min',
    prevArticle: 'admin-1', nextArticle: 'admin-3',
    sections: [
      {
        title: 'Inviter un nouveau membre',
        content: 'Prérequis : vous devez être Admin du workspace. Le membre invité doit avoir une adresse email valide.',
        steps: [
          { number: 1, title: 'Accéder', description: 'Allez dans la sidebar → "Équipe"' },
          { number: 2, title: 'Inviter', description: 'Cliquez sur le bouton "Inviter un membre"' },
          { number: 3, title: 'Email', description: 'Saisissez l\'adresse email du futur membre' },
          { number: 4, title: 'Rôle', description: 'Sélectionnez le rôle à attribuer (Admin / Manager / Viewer)' },
          { number: 5, title: 'Envoyer', description: 'Cliquez sur "Envoyer l\'invitation"' },
        ],
        tip: { type: 'info', content: 'Le membre invité reçoit un lien d\'invitation valable 72 heures. En cliquant ce lien, il crée son mot de passe et rejoint immédiatement le workspace.' },
        screenshotHint: '📸 Capture : modal d\'invitation avec le champ email et la sélection de rôle',
      },
      {
        title: 'Gérer les invitations en attente',
        content: 'La page Équipe affiche deux sections : les membres actifs et les invitations en attente.\n\nPour les invitations en attente :\n• Vous pouvez voir la date d\'expiration de chaque invitation\n• "Copier le lien" : copie le lien d\'invitation dans le presse-papiers\n• "Révoquer" : invalide le lien — le membre ne pourra plus rejoindre via ce lien',
        screenshotHint: '📸 Capture : section "Invitations en attente" avec un lien et le bouton Révoquer',
      },
      {
        title: 'Gérer les membres actifs',
        content: 'Sur chaque membre actif, vous disposez des actions suivantes.',
        table: {
          headers: ['Action', 'Effet', 'Réversible ?'],
          rows: [
            ['Changer le rôle', 'Modifie les permissions immédiatement', 'Oui — changez à nouveau le rôle'],
            ['Désactiver', 'Bloque la connexion, conserve les données', 'Oui — bouton "Réactiver"'],
            ['Supprimer', 'Supprime définitivement le compte', 'Non — action irréversible'],
          ],
        },
        tip: { type: 'tip', content: 'Préférez la désactivation à la suppression. Un compte désactivé conserve l\'historique des actions de l\'utilisateur et peut être réactivé à tout moment.' },
      },
    ],
  },
  {
    id: 'admin-3',
    categoryId: 'admin', categoryLabel: 'Administration', categoryColor: '#6B6050',
    guideNumber: 3, totalGuides: 4,
    title: 'Photo de profil et couverture',
    subtitle: 'Personnaliser votre avatar et la photo de couverture du workspace.',
    readTime: '3 min',
    prevArticle: 'admin-2', nextArticle: 'admin-4',
    sections: [
      {
        title: 'Photo de profil',
        content: 'Chaque utilisateur peut personnaliser sa photo de profil. Elle apparaît dans la sidebar, dans la page Équipe et sur la page d\'Accueil.',
        steps: [
          { number: 1, title: 'Accéder', description: 'Sur la page d\'Accueil, survolez votre photo de profil (ou l\'initiale dorée)' },
          { number: 2, title: 'Uploader', description: 'Cliquez sur l\'icône appareil photo qui apparaît au survol' },
          { number: 3, title: 'Sélectionner', description: 'Choisissez une image (JPG, PNG, max 5 Mo, ratio carré recommandé)' },
          { number: 4, title: 'Valider', description: 'La photo est mise à jour immédiatement' },
        ],
        tip: { type: 'tip', content: 'Utilisez une photo professionnelle ou un avatar distinctif. Cela aide vos collaborateurs à vous identifier rapidement dans l\'équipe.' },
        screenshotHint: '📸 Capture : hover sur la photo de profil avec l\'icône appareil photo',
      },
      {
        title: 'Photo de couverture du workspace',
        content: 'La photo de couverture est visible sur la page d\'Accueil de tous les membres du workspace. Elle donne une identité visuelle à votre organisation.\n\nSeuls les administrateurs peuvent modifier la photo de couverture.',
        steps: [
          { number: 1, title: 'Accéder', description: 'Sur la page d\'Accueil, cliquez sur "Changer la couverture" (bouton en bas à droite de la bannière)' },
          { number: 2, title: 'Sélectionner', description: 'Choisissez une image (JPG, PNG, min 1200×400px recommandé)' },
          { number: 3, title: 'Valider', description: 'La couverture est mise à jour pour tous les membres' },
        ],
        tip: { type: 'info', content: 'Si aucune couverture n\'est définie, TremorApp affiche un gradient élégant aux couleurs de la marque (onyx → teal → gold).' },
        screenshotHint: '📸 Capture : page d\'Accueil avec la bannière de couverture et le bouton "Changer la couverture"',
      },
    ],
  },
  {
    id: 'admin-4',
    categoryId: 'admin', categoryLabel: 'Administration', categoryColor: '#6B6050',
    guideNumber: 4, totalGuides: 4,
    title: 'Paramètres du workspace',
    subtitle: 'Nom, plan d\'abonnement, billing, notifications et intégrations Google.',
    readTime: '4 min',
    prevArticle: 'admin-3',
    sections: [
      {
        title: 'Profil utilisateur',
        content: 'Accessible dans Paramètres → section "Profil". Tout utilisateur peut modifier son nom. L\'email est en lecture seule (c\'est l\'identifiant de connexion).',
        screenshotHint: '📸 Capture : section Profil dans les Paramètres avec le champ Nom et l\'email grisé',
      },
      {
        title: 'Comptes Google connectés',
        content: 'Section visible pour les Admins et Managers. Permet de connecter Google Search Console et Google Business Profile pour alimenter les modules GFO et LLR.',
        steps: [
          { number: 1, title: 'Connecter', description: 'Cliquez "Connecter un compte Google"' },
          { number: 2, title: 'Autoriser', description: 'Connectez-vous à votre compte Google et autorisez TremorApp' },
          { number: 3, title: 'Vérifier', description: 'Le compte apparaît dans la liste avec le statut "Connecté"' },
        ],
        tip: { type: 'warning', content: 'Connectez un compte Google qui a accès à votre Search Console ET à votre fiche Google Business. Si ce sont des comptes différents, connectez les deux.' },
        screenshotHint: '📸 Capture : section "Comptes Google connectés" avec un compte affiché et son statut',
      },
      {
        title: 'Notifications',
        content: 'Configurez les alertes email et les notifications in-app. Trois toggles disponibles :\n\n• Alertes email (pics de demande, avis négatifs)\n• Rapport hebdomadaire par email\n• Notifications in-app',
      },
      {
        title: 'Abonnement et billing',
        content: 'Section réservée aux Admins. Affiche le plan actuel (Free, Starter, Pro) et permet de gérer l\'abonnement via Stripe.',
        table: {
          headers: ['Plan', 'Prix', 'Inclus'],
          rows: [
            ['Free', 'Gratuit', '1 Radar, 1 marché SDI, fonctionnalités de base'],
            ['Starter', '29€/mois', '5 Radars, 3 marchés, intégrations Google'],
            ['Pro', '79€/mois', 'Radars illimités, marchés illimités, actions IA, support prioritaire'],
          ],
        },
        screenshotHint: '📸 Capture : section Abonnement avec le badge du plan actuel et les boutons de mise à niveau',
      },
      {
        title: 'Espace de travail',
        content: 'Section réservée aux Admins. Permet de modifier le nom du workspace qui apparaît dans la sidebar et les rapports.',
      },
    ],
  },
]
